/**
 * Cache IndexedDB des étudiants pour recherche instantanée hors-ligne.
 *
 * Synchronisation toutes les 10 minutes (fetch complet — la liste est petite,
 * ~3000 étudiants ≈ 300 KB gzippés). Recherche 100 % locale sur nom, prénoms,
 * matricule, classe.
 */
import { getSession } from './sessionApi';

const DB_NAME = 'verif2i-cache';
const DB_VERSION = 1;
const STORE_ETU = 'etudiants';
const STORE_META = 'meta';

const SYNC_INTERVAL_MS = 10 * 60 * 1000; // 10 min
const META_KEY_LAST_SYNC = 'last_sync';

export interface EtudiantCache {
  m: string;  // matricule (clé primaire)
  n: string;  // nom
  p: string;  // prenoms
  c: string;  // classe
  l: string;  // niveau
  o: string;  // option
  // Champs dérivés pour la recherche (indexés)
  s?: string; // search string en minuscule (nom + prenoms + matricule + classe)
}

// ---------------------------------------------------------------------------
// IndexedDB helpers
// ---------------------------------------------------------------------------

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_ETU)) {
        const store = db.createObjectStore(STORE_ETU, { keyPath: 'm' });
        store.createIndex('search', 's', { unique: false });
      }
      if (!db.objectStoreNames.contains(STORE_META)) {
        db.createObjectStore(STORE_META);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function tx<T>(
  store: string,
  mode: IDBTransactionMode,
  fn: (s: IDBObjectStore) => IDBRequest<T> | Promise<T>,
): Promise<T> {
  const db = await openDB();
  return new Promise<T>((resolve, reject) => {
    const t = db.transaction(store, mode);
    const s = t.objectStore(store);
    const r = fn(s);
    if (r instanceof IDBRequest) {
      r.onsuccess = () => resolve(r.result as T);
      r.onerror = () => reject(r.error);
    } else {
      r.then(resolve, reject);
    }
    t.onerror = () => reject(t.error);
  });
}

// ---------------------------------------------------------------------------
// API publique
// ---------------------------------------------------------------------------

export async function getMeta<T = any>(key: string): Promise<T | null> {
  try {
    return await tx<T>(STORE_META, 'readonly', (s) => s.get(key) as IDBRequest<T>);
  } catch { return null; }
}

export async function setMeta(key: string, value: any): Promise<void> {
  await tx(STORE_META, 'readwrite', (s) => s.put(value, key));
}

export async function getLastSync(): Promise<number | null> {
  const v = await getMeta<number>(META_KEY_LAST_SYNC);
  return v ?? null;
}

export async function countEtudiants(): Promise<number> {
  try {
    return await tx<number>(STORE_ETU, 'readonly', (s) => s.count() as IDBRequest<number>);
  } catch { return 0; }
}

export async function clearCache(): Promise<void> {
  try {
    await tx(STORE_ETU, 'readwrite', (s) => s.clear());
    await tx(STORE_META, 'readwrite', (s) => s.clear());
  } catch { /* ignore */ }
}

/**
 * Récupère la liste complète depuis le serveur et remplace le contenu local.
 * Nécessite une session valide (envoi token via sessionApi).
 */
export async function syncFromServer(): Promise<{ count: number; syncedAt: number }> {
  const session = getSession();
  if (!session) throw new Error('Session requise pour synchroniser le cache');
  const res = await fetch('/api/public/verif/etudiants-cache/', {
    credentials: 'omit',
    headers: {
      Accept: 'application/json',
      'X-Recouvrement-Token': session.token,
    },
  });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try { const j = await res.json(); msg = j.detail || msg; } catch { /* */ }
    const err: any = new Error(msg);
    err.status = res.status;
    throw err;
  }
  const data = await res.json();
  const items: EtudiantCache[] = (data.etudiants || []).map((e: any) => ({
    m: e.m, n: e.n, p: e.p, c: e.c, l: e.l, o: e.o,
    s: `${e.n} ${e.p} ${e.m} ${e.c || ''}`.toLowerCase(),
  }));

  // Remplacement complet (bulk write dans une seule transaction)
  const db = await openDB();
  await new Promise<void>((resolve, reject) => {
    const t = db.transaction(STORE_ETU, 'readwrite');
    const s = t.objectStore(STORE_ETU);
    s.clear();
    for (const it of items) s.put(it);
    t.oncomplete = () => resolve();
    t.onerror = () => reject(t.error);
  });
  const syncedAt = Date.now();
  await setMeta(META_KEY_LAST_SYNC, syncedAt);
  return { count: items.length, syncedAt };
}

/**
 * Vérifie si une synchro est nécessaire (première fois OU intervalle dépassé).
 */
export async function needsSync(): Promise<boolean> {
  const last = await getLastSync();
  if (!last) return true;
  const count = await countEtudiants();
  if (count === 0) return true;
  return (Date.now() - last) > SYNC_INTERVAL_MS;
}

/**
 * Recherche 100 % locale (icontains sur nom, prénoms, matricule, classe).
 * Tokens split : chaque token doit apparaître dans la chaîne concaténée.
 */
export async function searchLocal(query: string, limit: number = 20): Promise<EtudiantCache[]> {
  const q = (query || '').trim().toLowerCase();
  if (q.length < 2) return [];
  const tokens = q.split(/\s+/).filter(t => t.length >= 1);
  if (tokens.length === 0) return [];

  const db = await openDB();
  return new Promise<EtudiantCache[]>((resolve, reject) => {
    const t = db.transaction(STORE_ETU, 'readonly');
    const s = t.objectStore(STORE_ETU);
    const req = s.openCursor();
    const results: EtudiantCache[] = [];
    req.onsuccess = () => {
      const cur = req.result;
      if (!cur || results.length >= limit) { resolve(results); return; }
      const e = cur.value as EtudiantCache;
      const hay = e.s || '';
      if (tokens.every(t => hay.includes(t))) results.push(e);
      cur.continue();
    };
    req.onerror = () => reject(req.error);
  });
}

/**
 * Récupère un étudiant par matricule (exact match, ultra-rapide).
 */
export async function getByMatricule(matricule: string): Promise<EtudiantCache | null> {
  const mat = (matricule || '').trim().toUpperCase();
  if (!mat) return null;
  try {
    const r = await tx<EtudiantCache | undefined>(
      STORE_ETU, 'readonly', (s) => s.get(mat) as IDBRequest<EtudiantCache | undefined>,
    );
    return r || null;
  } catch { return null; }
}
